import { createRoute, updateRoute } from "./routeService";

// Map UI frequency labels to gateway millisecond values
const FREQUENCY_MS = {
  none: 86400000, // irrelevant when noAlerts is true
  once: 86400000, // once per 24-hour period
  "15min": 900000,
  "30min": 1800000,
  "60min": 3600000,
};

const MPH_TO_MPS = 1 / 2.23694;

/**
 * Map the RouteEmailConfig UI output to the gateway's AlertPreferencesDto.
 *
 * The UI slider works in mph but the gateway stores speedThreshold in m/s
 * (1000 = threshold disabled, AlertsUser.NO_SPEED_PREFERENCE).  Omitted
 * quiet hours are cleared explicitly via clearNoIncidentsPeriod — sending
 * no noIncidentsPeriod leaves the stored one in place (gateway#10).
 *
 * @param {{ schedule: object, preferences: object }} alertConfig
 */
export const toAlertPreferencesDto = (alertConfig) => {
  const prefs = alertConfig.preferences || {};
  const reports = prefs.reportsToInclude || {};

  const schedule = alertConfig.schedule || {};

  // Build "HH:MM to HH:MM" strings for TimeOfDayRange
  const period =
    schedule.startTime && schedule.endTime
      ? `${schedule.startTime} to ${schedule.endTime}`
      : undefined;

  const quietHours = prefs.quietHours;
  const noIncidentsPeriod =
    quietHours?.enabled && quietHours.start && quietHours.end
      ? `${quietHours.start} to ${quietHours.end}`
      : undefined;

  const speedThresholdMph = prefs.speedThreshold ?? 1000;

  return {
    noAlerts: prefs.emailFrequency === "none",
    travelTime: !!reports.travelTime,
    congestion: !!reports.congestion,
    construction: !!reports.construction,
    incident: !!reports.incidents,
    weather: !!reports.weather,
    allSevereIncidents: !!prefs.receiveAllSevere,
    speedThreshold:
      speedThresholdMph >= 999 ? 1000 : speedThresholdMph * MPH_TO_MPS,
    frequency: FREQUENCY_MS[prefs.emailFrequency] || 86400000,
    daysOfWeek: schedule.daysOfWeek || undefined,
    period: period,
    noIncidentsPeriod: noIncidentsPeriod,
    clearNoIncidentsPeriod: !noIncidentsPeriod,
  };
};

/**
 * Save a route with alert configuration via UserTripJsonController.
 *
 * Creates a new route, or updates the existing one when routeId is given —
 * previously routeId was ignored and updating an edited route silently
 * created a duplicate (gateway#10).
 *
 * @param {{ displayName: string, waypoints: Array<{lat,lng}>, routeData: object, alertConfig: object, routeId?: number }} data
 */
export const saveRouteAlertConfig = async ({
  displayName,
  waypoints,
  routeData,
  alertConfig,
  routeId,
}) => {
  const preferences = toAlertPreferencesDto(alertConfig);

  if (routeId) {
    return updateRoute(routeId, {
      displayName,
      waypoints,
      preferences,
      routeData,
    });
  }
  return createRoute({ displayName, waypoints, preferences, routeData });
};
